/**
 * Contrato Option B — Tiger Keystore/Keychain → secreto → Auth clásico.
 * Solo parsers/validación (sin window). No loguear secretos.
 */

export const SECRET_TYPE_PASSWORD = 'password';
export const SECRET_TYPE_REFRESH = 'refresh';
export const SECRET_TYPES = Object.freeze([SECRET_TYPE_PASSWORD, SECRET_TYPE_REFRESH]);

export const MAX_EMAIL_LEN = 320;
export const MAX_SECRET_LEN = 8192;

export const BRIDGE_CODES = Object.freeze({
  WEB_SKIP: 'WEB_SKIP',
  BRIDGE_UNAVAILABLE: 'BRIDGE_UNAVAILABLE',
  TIMEOUT: 'TIGER_BIOMETRIC_CREDENTIAL_TIMEOUT',
  CANCELLED: 'TIGER_BIOMETRIC_CREDENTIAL_CANCELLED',
  NO_SECRET: 'TIGER_BIOMETRIC_CREDENTIAL_MISSING',
  INVALID_PAYLOAD: 'TIGER_BIOMETRIC_CREDENTIAL_INVALID',
  EMAIL_MISMATCH: 'TIGER_BIOMETRIC_CREDENTIAL_EMAIL_MISMATCH',
  SAVE_FAILED: 'TIGER_BIOMETRIC_CREDENTIAL_SAVE_FAILED',
  CLEAR_FAILED: 'TIGER_BIOMETRIC_CREDENTIAL_CLEAR_FAILED',
});

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {'password'|'refresh'|null}
 */
export function normalizeSecretType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === SECRET_TYPE_PASSWORD || raw === SECRET_TYPE_REFRESH) return raw;
  return null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeSecret(value) {
  if (typeof value !== 'string') return '';
  return value;
}

/**
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmailShape(email) {
  const e = normalizeEmail(email);
  if (!e || e.length > MAX_EMAIL_LEN) return false;
  // Suficiente para bridge; Auth valida de verdad.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/**
 * Parsea payload nativo (objeto, JSON string, o args posicionales legacy-friendly).
 * Nunca incluye el secreto en mensajes de error.
 *
 * @param {unknown} okRaw
 * @param {unknown} payloadOrDescription
 * @param {unknown} [errorCode]
 * @returns {{
 *   ok: boolean,
 *   email: string,
 *   secretType: 'password'|'refresh'|null,
 *   secret: string,
 *   description: string,
 *   errorCode: string|number|null,
 *   code: string|null,
 * }}
 */
export function parseUnlockResult(okRaw, payloadOrDescription, errorCode) {
  const ok = Boolean(okRaw);
  let payload = payloadOrDescription;

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        payload = JSON.parse(trimmed);
      } catch {
        /* description plana */
      }
    }
  }

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const email = normalizeEmail(payload.email ?? payload.EmailAddress);
    const secretType = normalizeSecretType(
      payload.secret_type ?? payload.secretType ?? payload.type
    );
    const secret = normalizeSecret(payload.secret ?? payload.credential ?? payload.value);
    const description = String(
      payload.description ?? payload.message ?? payload.error ?? ''
    ).trim();
    const code =
      payload.code
      ?? payload.error_code
      ?? payload.errorCode
      ?? errorCode
      ?? null;

    return {
      ok,
      email,
      secretType,
      secret: ok ? secret : '',
      description,
      errorCode: code == null || code === '' ? null : code,
      code: null,
    };
  }

  return {
    ok,
    email: '',
    secretType: null,
    secret: '',
    description: String(payloadOrDescription ?? '').trim(),
    errorCode: errorCode == null || errorCode === '' ? null : errorCode,
    code: null,
  };
}

/**
 * Valida resultado de unlock listo para Auth. Vacía secret en fallos.
 *
 * @param {ReturnType<typeof parseUnlockResult>} parsed
 * @param {string} expectedEmail
 */
export function assertUnlockForLogin(parsed, expectedEmail) {
  const expected = normalizeEmail(expectedEmail);
  if (!parsed.ok) {
    const cancelCodes = [10, -2, -4, '10', '-2', '-4', BRIDGE_CODES.CANCELLED];
    if (cancelCodes.includes(parsed.errorCode) || /cancel/i.test(parsed.description)) {
      return {
        ok: false,
        code: BRIDGE_CODES.CANCELLED,
        message: null,
        email: expected,
        secretType: null,
        secret: '',
      };
    }
    if (/not_available|no.?secret|missing|sin secreto/i.test(parsed.description)) {
      return {
        ok: false,
        code: BRIDGE_CODES.NO_SECRET,
        message: 'No se pudo verificar tu acceso biométrico. Ingresá con usuario y contraseña.',
        email: expected,
        secretType: null,
        secret: '',
      };
    }
    return {
      ok: false,
      code: BRIDGE_CODES.INVALID_PAYLOAD,
      message: 'No se pudo verificar tu acceso biométrico. Ingresá con usuario y contraseña.',
      email: expected,
      secretType: null,
      secret: '',
    };
  }

  if (!isValidEmailShape(expected)) {
    return {
      ok: false,
      code: BRIDGE_CODES.INVALID_PAYLOAD,
      message: 'Ingresá un email válido.',
      email: expected,
      secretType: null,
      secret: '',
    };
  }

  if (parsed.email && parsed.email !== expected) {
    return {
      ok: false,
      code: BRIDGE_CODES.EMAIL_MISMATCH,
      message: 'No se pudo verificar tu acceso biométrico. Ingresá con usuario y contraseña.',
      email: expected,
      secretType: null,
      secret: '',
    };
  }

  if (!parsed.secretType || !SECRET_TYPES.includes(parsed.secretType)) {
    return {
      ok: false,
      code: BRIDGE_CODES.INVALID_PAYLOAD,
      message: 'Respuesta biométrica incompleta del dispositivo.',
      email: expected,
      secretType: null,
      secret: '',
    };
  }

  if (!parsed.secret || parsed.secret.length > MAX_SECRET_LEN) {
    return {
      ok: false,
      code: BRIDGE_CODES.INVALID_PAYLOAD,
      message: 'Respuesta biométrica incompleta del dispositivo.',
      email: expected,
      secretType: null,
      secret: '',
    };
  }

  return {
    ok: true,
    code: null,
    message: null,
    email: expected,
    secretType: parsed.secretType,
    secret: parsed.secret,
  };
}

/**
 * @param {string|null|undefined} message
 * @returns {string}
 */
export function redactSecretsInMessage(message) {
  // Defensa en profundidad si algo filtró un token largo.
  return String(message || '')
    .replace(/(Bearer\s+)[A-Za-z0-9\-._~+/]+=*/gi, '$1[REDACTED]')
    .replace(/(secret["']?\s*[:=]\s*["']?)[^"'&\s]+/gi, '$1[REDACTED]');
}
